<template>
  <div
    ref="image"
    class="image"
  >
    <img
      v-if="image && image.src"
      class="image__img"
      :alt="imageAlt"
      decoding="async"
      :fetchpriority="fetchPriority"
      :loading="loading"
      :role="role"
      :sizes="getSizes()"
      :src="getSrc()"
      :srcset="getSrcset().srcset"
    >
  </div>
</template>

<script>

/**
 * Component: Responsive image
 * -----------------------------------------------------------------------------
 * - Core template for inline Responsive Images
 * - Use this component for responsive images - showing the same image at
 *   all resolutions but serving appropriately sized images for the viewport.
 * - Styles are loaded in compiled SCSS.
 * - 'Image Element' (aka Art Direction) should be used for displaying different
 *   images on mobile and desktop.
 *
 * @param {String} [alt] - Override image alt.
 * @param {String} [aspectRatio] - Override image aspect ratio.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Object} [fetchPriority] - Fetch Priority attribute, either 'high', 'low', or 'auto'.
 * @param {Object} image - A formatted image object.
 * @param {String} [loading] - Loading attribute, either `lazy` or `eager`.
 * @param {String} [minMax] - Min and max size for srcset in `min-max` format.
 * ratio, default to true, disable to make image fill its parent container.
 * @param {String} [sizes] - Value for sizes attribute.
 *
 */
import {
  getCloudinarySizedImageUrl,
  getImageSrcset,
  getSizedImageUrl,
} from '~/helpers/format'

export default {
  name: 'ResponsiveImage',

  props: {
    alt: {
      type: String,
      default: '',
    },
    aspectRatio: {
      type: [Boolean, String],
      default: false,
    },
    fetchPriority: {
      type: String,
      default: 'auto',
    },
    image: {
      type: [Boolean, Object],
      default: false,
    },
    loading: {
      type: String,
      default: 'lazy',
    },
    minMax: {
      type: String,
      default: '200-1000',
    },
    sizes: {
      type: String,
      default: 'auto',
    },
  },

  emits: ['set-aspect-ratio'],

  computed: {

    /**
     * Calculate the image alt.
     * - Attempts to retrieve from the image object but displays
     *   the override prop if present.
     * @returns {String}
     */
    imageAlt() {
      const imageAlt = this.alt || this.image.alt
      return imageAlt ? imageAlt : ''
    },

    /**
     * Calculate an accessible, SEO friendly role.
     * @returns {String|Null}
     */
    role() {
      if (this.imageAlt) {
        return null
      }

      return 'presentation'
    },
  },

  mounted() {
    this.setImageProperties()
  },

  methods: {

    /**
     * Set the aspect ratio as a CSS attribute to size the container.
     * @returns {String}
     */
    setImageProperties() {
      const aspectRatio = this.aspectRatio || this.image.aspectRatio

      if (!aspectRatio) {
        return
      }

      this.$refs.image.style.setProperty(
        '--aspect-ratio',
        aspectRatio,
      )

      this.$emit('set-aspect-ratio', aspectRatio)
    },

    /**
     * Get sizes attribute.
     * - Can either be sizes or data-sizes based on lazyload.
     * @returns {String|Null}
     */
    getSizes() {
      return this.sizes
    },

    /**
     * Get src URL.
     * @returns {String|Null}
     */
    getSrc() {
      if (!this.image.src) {
        return null
      }

      if (this.image.source == 'cloudinary') {

        /// Cloudinary Transformations
        /// q_auto - optimal balance between file size and visual quality
        /// f_auto - Optimal file format selection based on browser compatibility
        /// c_scale - Scale the Image
        /// w_{Size} - Requested Image width
        const transformations = [
          'q_auto', 'f_auto', 'c_scale', `w_${this.getSrcset().placeholder}`,
        ]

        const baseUrl = this.image.baseUrl
        const publicId = this.image.publicId

        return getCloudinarySizedImageUrl({ baseUrl, publicId, transformations })
      }

      return getSizedImageUrl(this.image.src, `${this.getSrcset().placeholder}x`)
    },

    /**
     * Get srcset for an image object.
     * @returns {Object}
     */
    getSrcset() {
      return getImageSrcset(this.image, this.minMax)
    },
  },
}
</script>
